import React, { useEffect } from 'react';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { IContentTypeEntries } from '@constants/metas/default';
import { getOrCreateLDCookiesSsr } from '@components/bucketTest/bucket.utils';
import CMSPage from '@components/organisms/content-stack/cms-page';
import { ContentStackPageNames } from '@enums/contentStack';
import { useAnalytics } from '@context/analytics/analytics.context';
import extractFromContext from '@helpers/utils/extract-from-context';
import ContentStackService from '@services/content-stack-service';
import logger from '@helpers/utils/logger/client';
import DefaultLayout from '@layouts/default-layout';
import StickyNavEntryPointWrapper from '@components/molecules/sticky-nav-entry-point/sticky-nav-entry-point-wrapper';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import RequestError from '@errors/request-error';
import UserService from '@services/user-service';
import { User } from '@interfaces/models/user';
import { checkShouldShowBanner } from '@helpers/utils/general';
import useRestoreHpScrollPosition from '@hooks/cms/use-restore-hp-scroll-position';
import { getAppInitialProps } from '@helpers/initial-props';
import { isFromStickyNav, getStickyUniverse } from '@helpers/utils/sticky-nav/sticky-nav-utils';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import getServerFeatureFlag from '@hooks/bucket-test/use-ssr-feature-flag';
import { useShoppingPreferences } from '@hooks/user/use-shopping-preferences';
import { ShoppingPreferenceModal } from '@components/user-preferences-modal/user-shopping-preferences-modal';
import { ShoppingPrefsPayload } from '@services/user-shopping-preferences-service';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { prefetchAndSetStickyNavData } from '@helpers/utils/sticky-nav/sticky-nav-prefetch';

const ZendeskWebWidget = dynamic(() => import('@components/molecules/zendesk-web-widget'), { ssr: false });

type HomePageProps = {
  contentTypeEntries: IContentTypeEntries;
  universe: '1' | '2' | null;
  shouldUpdatePrefs: boolean;
};

const HomePage: NextPageWithLayout<HomePageProps> = (props) => {
  const { contentTypeEntries, universe, shouldUpdatePrefs } = props;
  const { t: tA11y } = useTranslation('a11y');
  const shouldShowBanner = checkShouldShowBanner(contentTypeEntries);
  const { hpScrollPosition, scrollToCmsElementId } = useRestoreHpScrollPosition();
  const { preferences, persistedPreferences, source, shoppingPrefEnable, hasUserSelectedPrefs, setPreferences } =
    useShoppingPreferences();
  const router = useRouter();
  const { updateDataLayer } = useAnalytics();

  useEffect(() => {
    if (!!hpScrollPosition) {
      scrollToCmsElementId(hpScrollPosition);
    }
  }, []);

  // Remove sticky nav params from URL after landing so subsequent shopping pref change (Men–Women in ME menu) works.
  // Use history.replaceState instead of router.replace to avoid triggering routeChangeComplete (which reloads TagCommander and fires a duplicate pageview).
  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    const { fromStickyNav, stickyUniverse } = router.query;
    if (fromStickyNav === '1' && (stickyUniverse === '1' || stickyUniverse === '2')) {
      const pathWithHash = window.location.pathname + (window.location.hash || '');
      window.history.replaceState(null, '', pathWithHash);
    }
  }, [router.isReady, router.query]);

  useEffect(() => {
    updateDataLayer({
      user_shopping_preferences: `{ "universe": "${
        hasUserSelectedPrefs && !!persistedPreferences?.universe ? persistedPreferences?.universe : null
      }" }`,
    });
  }, [persistedPreferences?.universe, hasUserSelectedPrefs]);

  useEffect(() => {
    if (shoppingPrefEnable && shouldUpdatePrefs) {
      setPreferences({ ...preferences, universe: parseInt(universe) });
      // Remove param to update prefs after updating the pref
      // if (router?.query?.updatePrefs) {
      //   router.replace(`/?universe=${universe}`);
      //}
    }
  }, [shoppingPrefEnable, shouldUpdatePrefs, universe]);

  return (
    <DefaultLayout shouldShowBanner={shouldShowBanner}>
      <StickyNavEntryPointWrapper
        stickyNavTestGroup={contentTypeEntries?.testGroup}
        universe={universe ? parseInt(universe, 10) : undefined}
      />
      <h1 className="visuallyhidden">{tA11y('A11Y.LOGO_HEADING')}</h1>
      <CMSPage {...{ contentTypeEntries, preferences, preferenceSource: source }} />
      <ZendeskWebWidget />
      {shoppingPrefEnable && <ShoppingPreferenceModal />}
    </DefaultLayout>
  );
};

HomePage.Layout = [];
HomePage.PageType = 'HomePage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const queryClient = new QueryClient();
    const initialProps = await getAppInitialProps(context, queryClient);

    const { preferences, locale, sessionId, userId, serverAxiosRequestData, userShoppingPreferences } =
      extractFromContext(context);
    const cmsFallback = context?.query?.cmsFallback === 'true';
    const { serverAnonymousId, randomRangeId } = getOrCreateLDCookiesSsr(context);
    const { getFeatureFlag } = getServerFeatureFlag(context, serverAnonymousId, randomRangeId);
    let serverAnonymousIdWithTimestamp: string = null;
    const query = context?.query ?? {};
    const stickyUniverseVal = getStickyUniverse(query);
    const universe =
      isFromStickyNav(query) && stickyUniverseVal
        ? String(stickyUniverseVal)
        : context?.query?.universe ?? userShoppingPreferences?.universe ?? null;
    const shouldUpdatePrefs = context?.query?.updatePrefs === '1';
    let shoppingPrefsHeader: ShoppingPrefsPayload | null = null;

    if (universe) {
      shoppingPrefsHeader = { universe: parseInt(universe as string, 10) };
    }

    try {
      await getFeatureFlag('test-server-side-flag-evaluation');
    } catch (error) {
      logger.error(error, 'Error fetching test-server-side-flag-evaluation feature flag');
    }

    if (sessionId && userId) {
      try {
        const MILLISECONDS_PER_SECOND = 1000;
        const user: User = await UserService.getUser(userId, serverAxiosRequestData);
        if (user?.inscriptionDate) {
          const inscriptionDateAsTimestampInSeconds =
            new Date(user.inscriptionDate).getTime() / MILLISECONDS_PER_SECOND;
          serverAnonymousIdWithTimestamp = `${serverAnonymousId};${inscriptionDateAsTimestampInSeconds}`;
        }
      } catch (e) {
        logger.error(`Error fetching user data: ${e}`);
      }
    }

    const apiRequests = [
      serverSideTranslations(locale, ['default-layout', 'vc-authentication', 'a11y', 'common', 'shopping-preferences']),
      ContentStackService.getDataForCMSPage(
        ContentStackPageNames.HOME_PAGE,
        {
          ...preferences,
          userId,
          serverAnonymousId: serverAnonymousIdWithTimestamp ?? serverAnonymousId,
          csBranch: context?.query?.csBranch as string,
          shoppingPreferences: shoppingPrefsHeader,
          userAgent: context?.req?.headers?.['user-agent'],
        },
        { cmsFallback },
      ),
    ];

    const promiseResults = await Promise.allSettled(apiRequests);

    const [translations, contentTypeEntries] = promiseResults.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      }

      if (!cmsFallback && index === 1) {
        throw new RequestError(result.reason);
      }

      return null;
    });

    if (!contentTypeEntries && cmsFallback) {
      return {
        redirect: {
          destination: '/new-items/',
          permanent: false,
        },
      };
    }

    const stickyNavTestGroup = contentTypeEntries?.testGroup ?? 'control';
    await prefetchAndSetStickyNavData(context, initialProps, queryClient, getFeatureFlag, stickyNavTestGroup);

    return {
      props: {
        ...initialProps,
        ...translations,
        contentTypeEntries: contentTypeEntries || {},
        universe,
        shouldUpdatePrefs,
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (err) {
    if (isRequestError(err)) {
      if (!isRequest404Error(err)) {
        logger.error(err, 'There was an API request error on the Home Page - getServerSideProps - index.page.tsx');
      }
      return {
        notFound: true,
      };
    }
    logger.error(err, 'The server failed to render the Home Page - getServerSideProps - index.page.tsx');
    throw err;
  }
};

export default HomePage;
